'use client';

import { useState, useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface Project {
  id: number;
  title: string;
  category: string;
  location: string;
  image: string;
  description: string;
}

const FeaturedProjects = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [hoveredProject, setHoveredProject] = useState<number | null>(null);
  const [selectedFilter, setSelectedFilter] = useState<string>('all');
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [isAutoScrollPaused, setIsAutoScrollPaused] = useState(false);
  const animationFrameRef = useRef<number | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  // Smooth infinite auto-scroll effect
  useEffect(() => {
    if (!isHydrated || isAutoScrollPaused) return;

    let lastTimestamp = 0;
    const scrollSpeed = 0.5; // pixels per frame

    const animate = (timestamp: number) => {
      if (!scrollContainerRef.current) return;

      if (lastTimestamp) {
        const container = scrollContainerRef.current;
        
        // Scroll by a small amount
        container.scrollLeft += scrollSpeed;

        // Get the scroll position and container width
        const scrollLeft = container.scrollLeft;
        const scrollWidth = container.scrollWidth;
        const clientWidth = container.clientWidth;
        
        // Calculate where one set of projects ends (half of total width since we duplicate)
        const oneSetWidth = scrollWidth / 3; // We have 3 copies

        // When we've scrolled past one full set, reset to the start of the second set
        if (scrollLeft >= oneSetWidth) {
          container.scrollLeft = scrollLeft - oneSetWidth;
        }
      }

      lastTimestamp = timestamp;
      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isHydrated, isAutoScrollPaused]);

  const projects: Project[] = [
    {
      id: 1,
      title: "Modern Minimalist Apartment",
      category: "Apartment",
      location: "Hebbal - Bangalore",
      image: "https://images.unsplash.com/photo-1721613883249-6242e2effbf3",
      description: "Contemporary 3BHK transformation blending aesthetics with function"
    },
    {
      id: 2,
      title: "Eco-Conscious Apartment",
      category: "Apartment",
      location: "Sarjapur - Bangalore",
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1465d717b-1764654993782.png",
      description: "Sustainable living space with recycled materials and biophilic design"
    },
    {
      id: 3,
      title: "Serene Sanctuary Villa",
      category: "Villa",
      location: "Whitefield, Bangalore",
      image: "https://images.unsplash.com/photo-1721614463238-c327d3d42ccf",
      description: "Contemporary villa blending modern aesthetics with traditional craftsmanship"
    },
/*    {
      id: 4,
      title: "Luxury Family Villa",
      category: "Villa",
      location: "Jubilee Hills, Hyderabad",
      image: "https://images.unsplash.com/photo-1718164258728-839e6060e489",
      description: "Opulent villa featuring custom Italian marble and multi-generational spaces"
    },*/
    {
      id: 5,
      title: "The Alabaster Home",
      category: "Apartment",
      location: "Purva Atmosphere Bangalore | Puravankara Thanisandra Main Road",
      image: "/Alabaster-Home/2.png",
      description: "Contemporary apartment with a soft alabaster palette and layered textures"
    },
    {
      id: 6,
      title: "The Statura Residence",
      category: "Apartment",
      location: "Krishvi Statura, Bangalore",
      image: "/The-Statura-Residence/1.jpg",
      description: "Contemporary apartment with clean lines, warm textures, and functional luxury"
    },
    {
      id: 7,
      title: "The Willow Residence",
      category: "Apartment",
      location: "Purva Atmosphere Bangalore | Puravankara Thanisandra Main Road",
      image: "/The-Willow-Residence/willow_0.jpg",
      description: "Warm, contemporary apartment blending natural textures with soft, livable interiors"
    }
  ];

  // Filter projects based on selected filter
  const filteredProjects = projects.filter((project) => {
    if (selectedFilter === 'all') return true;
    if (selectedFilter === 'apartment') return project.category === 'Apartment';
    if (selectedFilter === 'house') return project.category === 'House';
    if (selectedFilter === 'contemporary') return (
      project.title.toLowerCase().includes('contemporary') || project.description.toLowerCase().includes('contemporary')
    );
    return true;
  });

  // Create infinite scroll by tripling the array
  const infiniteProjects = [...filteredProjects, ...filteredProjects, ...filteredProjects].map((project, index) => ({
    ...project,
    uniqueId: `${project.id}-${index}`
  }));

  if (!isHydrated) {
    return (
      <section className="py-20 bg-zinc-50">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-20">
          <div className="h-12 bg-white animate-pulse w-64 mx-auto rounded" />
        </div>
      </section>
    );
  }

  return (
    <>
      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
      <section className="py-12 md:py-16 lg:py-20 bg-zinc-50">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 lg:px-20">
        {/* Section Header */}
        <div className="text-center mb-8 md:mb-12">
          <div className="inline-flex items-center gap-2.5 px-4 md:px-5 py-2 md:py-2.5 rounded-full bg-white/80 backdrop-blur-sm border border-black/10 mb-4 md:mb-6 will-change-transform">
            <div className="w-1.5 h-1.5 bg-black rounded-full" />
            <span className="text-xs font-medium text-black/90 tracking-widest uppercase">
              Our Portfolio
            </span>
          </div>
          <h2 className="font-headline text-3xl md:text-4xl lg:text-6xl font-normal text-black mb-4 md:mb-6 tracking-tight">
            Featured Projects
          </h2>
          <p className="text-base md:text-lg text-black/60 max-w-2xl mx-auto font-normal px-4">
            Curated residential transformations across Bangalore
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center justify-center gap-2 md:gap-4 mb-6 md:mb-8 px-2">
          {[
            { key: 'all', label: 'All Projects', icon: 'Squares2X2Icon' },
            { key: 'apartment', label: 'Apartments', icon: 'BuildingOffice2Icon' },
            { key: 'house', label: 'Houses', icon: 'HomeIcon' },
            { key: 'contemporary', label: 'Contemporary', icon: 'SparklesIcon' },
          ].map((f) => {
            const active = selectedFilter === f.key;
            return (
              <button
                key={f.key}
                onClick={() => setSelectedFilter(f.key)}
                className={`inline-flex items-center gap-2 md:gap-3 px-4 md:px-5 py-2.5 md:py-3 rounded-full border transition-all duration-200 text-xs md:text-sm font-medium active:scale-95 ${
                  active 
                    ? 'bg-success text-success-foreground border-success shadow-soft' 
                    : 'bg-white text-foreground border-border hover:border-success/30 hover:shadow-sm'
                }`}
              >
                <Icon name={f.icon} size={16} className={`${active ? 'text-success-foreground' : 'text-foreground/70'} w-3.5 h-3.5 md:w-4 md:h-4`} />
                <span>{f.label}</span>
              </button>
            );
          })}
        </div>

        {/* Projects Carousel */}
        <div className="relative">
          {/* Scrollable Container */}
          <div
            ref={scrollContainerRef}
            onMouseEnter={() => setIsAutoScrollPaused(true)}
            onMouseLeave={() => setIsAutoScrollPaused(false)}
            className="flex gap-4 md:gap-6 overflow-x-auto scrollbar-hide px-2 md:px-8"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {infiniteProjects.map((project) => (
                <div
                  key={project.uniqueId}
                  className="group relative overflow-hidden bg-white rounded-2xl transition-all duration-500 hover:shadow-2xl flex-shrink-0 w-[280px] sm:w-[320px] md:w-[400px]"
                  onMouseEnter={() => setHoveredProject(project.id)}
                  onMouseLeave={() => setHoveredProject(null)}
                >
                  {/* Image */}
                  <div className="relative h-[320px] sm:h-[360px] md:h-[400px] overflow-hidden">
                    <AppImage
                      src={project.image}
                      alt={project.title}
                      fill
                      className="object-cover transition-all duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  </div>

                  {/* Content */}
                  <div className="absolute inset-0 p-5 md:p-8 flex flex-col justify-end">
                    <div className="relative z-10">
                      <div className="transform transition-transform duration-500 group-hover:-translate-y-16">
                        <div className="flex flex-wrap items-center gap-2 mb-2 md:mb-3">
                          <span className="px-2.5 md:px-3 py-0.5 md:py-1 bg-white/20 backdrop-blur-md border border-white/30 rounded-full text-xs font-medium text-white">
                            {project.category}
                          </span>
                          <span className="text-white/60 text-xs md:text-sm">•</span>
                          <span className="text-white/80 text-xs md:text-sm font-normal line-clamp-1">{project.location}</span>
                        </div>

                        <h3 className="font-headline text-xl md:text-2xl font-normal text-white mb-1.5 md:mb-2">
                          {project.title}
                        </h3>
                      </div>

                      <div className="absolute left-0 right-0 bottom-0 transform translate-y-8 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                        <p className="text-white/70 font-normal text-xs md:text-sm leading-relaxed mb-3 md:mb-4 line-clamp-2">
                          {project.description}
                        </p>
                        <a href="/our-projects" className="inline-flex items-center gap-2 text-white font-medium text-xs md:text-sm">
                          <span>View Project</span>
                          <Icon name="ArrowRightIcon" size={14} className="md:w-4 md:h-4" />
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-12 md:mt-16">
          <a
            href="/our-projects"
            className="inline-flex items-center gap-2 md:gap-3 px-8 md:px-10 py-4 md:py-5 bg-black text-white font-medium text-sm md:text-base rounded-full transition-all duration-500 hover:scale-105 hover:shadow-2xl hover:shadow-black/20 active:scale-95"
          >
            <span>View All Projects</span>
            <Icon name="ArrowRightIcon" size={18} />
          </a>
        </div>
      </div>
    </section>
    </>
  );
};

export default FeaturedProjects;